import moment from "moment";
import { LookupStore } from "Stores/Domain/LookupStore";
import { StudentTargetModel } from "Views/Shared/Views/StudentTarget/StudentTargetModel";
import { StudentSubTargetModel } from "Views/Shared/Views/StudentSubTarget/StudentSubTargetModel";
import {
    UpsertStudentSessionRequest,
    UpsertSessionDocumentRequest,
    UpsertStudentSessionTargetRequest,
    UpsertStudentSessionSubTargetRequest,
    UpsertStudentSessionAQACertificateRequest,
    UpsertStudentSessionBehaviourRequest,
} from "./Request";
import { container } from "tsyringe";
import { StudentSessionContext } from "Shared/Models/StudentSessionReport";
import { StudentSessionLegacyFormModel } from "Views/Shared/Views/StudentSessionLegacyForm/StudentSessionLegacyFormModel";

export class UpsertStudentSessionRequestBuilder {
    private lookupStore = container.resolve(LookupStore);

    constructor() {}

    public build(
        model: StudentSessionContext,
        legacyOverrides?: Pick<StudentSessionLegacyFormModel, "sessionPlan" | "subTargets" | "mentoring"> | null,
        options?: { isAdmin?: boolean },
    ): UpsertStudentSessionRequest {
        const request = new UpsertStudentSessionRequest();
        this.mapSessionCoreFields(request, model, options);
        this.mapSessionTimes(request, model);
        this.mapSessionAbsence(request, model);
        this.mapSessionDocuments(request, model);
        this.mapSessionTargets(request, model);
        this.mapSessionSubTargets(request, model);
        this.mapSessionBehaviours(request, model);
        this.mapSessionAQACertificates(request, model);
        this.mapSessionLegacyOverrides(request, legacyOverrides);
        request.isAdmin = options?.isAdmin ?? true;
        return request;
    }

    private getSessionStatusId(value: string): string {
        const sessionStatus = this.lookupStore.sessionStatusKeyValuePairs.find((m) => m.text === value);
        return sessionStatus ? sessionStatus.key : "";
    }

    private mapSessionCoreFields(request: UpsertStudentSessionRequest, model: StudentSessionContext, options?: { isAdmin?: boolean }): void {
        request.id = model.id;
        request.studentId = model.student?.id ?? model.studentId ?? "";
        //todo magic strings!!! needs fixing
        const isAdmin = options?.isAdmin ?? true;
        if (model.id && model.sessionStatusId) {
            request.sessionStatusId = model.sessionStatusId;
        } else if (model.attendance === "1" && isAdmin) {
            request.sessionStatusId = this.getSessionStatusId("Pending");
        } else {
            request.sessionStatusId = this.getSessionStatusId("Submitted");
        }
        request.mentorId = model.mentorId;
        request.activityTypeId = model.activityTypeId;
        request.sessionDate = model.sessionDate;
        request.didAttend = model.attendance !== "0";
        request.reportIncident = model.reportIncident;
        request.wellbeingId = model.wellbeingId;
        request.involvementId = model.involvementId;
        request.sessionPlan = model.sessionPlan;
        request.sessionFeedback = model.sessionFeedback;
    }

    private mapSessionTimes(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        const convertToDate = (value: string | Date | null): string | null => {
            if (!value) return null;
            const updatedValue = value ? value : moment().format("HH:mm");
            const initialMoment = moment(updatedValue, "HH:mm");
            const initialDate = new Date();
            initialDate.setHours(initialMoment.hours());
            initialDate.setMinutes(initialMoment.minutes());
            return initialDate.toISOString();
        };
        request.sessionStartTime = model.sessionStartTime ? convertToDate(model.sessionStartTime as string) : null;
        request.sessionEndTime = model.sessionEndTime ? convertToDate(model.sessionEndTime as string) : null;
    }

    private mapSessionAbsence(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        request.studentSessionAbsenceRequest.studentSessionId = model.id;
        request.studentSessionAbsenceRequest.id = model.studentAbsence.id;
        request.studentSessionAbsenceRequest.absenseTypeId = model.studentAbsence.absenseTypeId;
        request.studentSessionAbsenceRequest.reason = model.studentAbsence.reason;
    }

    private mapSessionDocuments(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        request.sessionDocumentRequest = model.sessionDocument.map((sessionDocument) => {
            const dto = new UpsertSessionDocumentRequest();
            sessionDocument.toRequest(dto);
            return dto;
        });
    }

    private mapSessionTargets(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        request.studentSessionTargetRequests = model.studentTargets
            .slice()
            .filter((st: StudentTargetModel) => st.subTargets.some((x: StudentSubTargetModel) => x.isCompletedByMentor))
            .map((st) => this.buildSessionTargetRequest(st, model));
    }

    private buildSessionTargetRequest(st: StudentTargetModel, model: StudentSessionContext): UpsertStudentSessionTargetRequest {
        const { studentSessionTargetModel } = st;
        const targetRequest = new UpsertStudentSessionTargetRequest();
        if (studentSessionTargetModel!.isNew) {
            targetRequest.studentSessionId = model.id;
            targetRequest.studentTargetId = st.id!.toString();
        } else {
            studentSessionTargetModel!.toRequest(targetRequest);
        }
        return targetRequest;
    }

    private mapSessionSubTargets(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        request.studentSessionSubTargetRequests = model.studentTargets.flatMap((st) =>
            st.subTargets
                .filter((subTarget: StudentSubTargetModel) => subTarget.isCompletedByMentor)
                .map((sst: StudentSubTargetModel) => this.buildSessionSubTargetRequest(sst, st)),
        );
    }

    private buildSessionSubTargetRequest(subTarget: StudentSubTargetModel, parentTarget: StudentTargetModel): UpsertStudentSessionSubTargetRequest {
        const { studentSessionSubTargetModel } = subTarget;
        const dto = new UpsertStudentSessionSubTargetRequest();
        dto.id = studentSessionSubTargetModel!.isNew ? null : studentSessionSubTargetModel!.id;
        dto.studentTargetId = studentSessionSubTargetModel!.isNew
            ? subTarget.studentTargetId
            : parentTarget.id ?? parentTarget.studentSessionTargetModel?.studentTargetId ?? subTarget.studentTargetId ?? "";
        dto.studentSubTargetId = studentSessionSubTargetModel!.isNew ? subTarget.id! : studentSessionSubTargetModel!.studentSubTargetId;
        dto.studentSessionTargetId = studentSessionSubTargetModel!.isNew ? null : studentSessionSubTargetModel!.studentSessionTargetId;
        dto.mentoring = studentSessionSubTargetModel!.mentoring;
        dto.sessionPlan = studentSessionSubTargetModel!.sessionPlan;
        dto.detail = studentSessionSubTargetModel!.detail;
        dto.score = studentSessionSubTargetModel!.score;
        return dto;
    }

    private mapSessionBehaviours(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        const behaviours = model.studentBehaviourModels;
        if (behaviours && model.student?.receivingTargetedIntervention) {
            request.studentSessionBehaviourRequests = behaviours.map((m) => {
                const dto = new UpsertStudentSessionBehaviourRequest();
                m.studentSessionBehaviour.toRequest(dto);
                return dto;
            });
        }
    }

    private mapSessionAQACertificates(request: UpsertStudentSessionRequest, model: StudentSessionContext): void {
        request.studentSessionAQACertificateRequests = model.studentSessionAQACertificatesModel.certificates.map((s) => {
            const dto = new UpsertStudentSessionAQACertificateRequest();
            dto.studentSessionId = model.id;
            dto.id = s.id;
            dto.description = s.description;
            dto.isDeleted = s.isDeleted;
            return dto;
        });
    }

    private mapSessionLegacyOverrides(
        request: UpsertStudentSessionRequest,
        legacyOverrides?: Pick<StudentSessionLegacyFormModel, "sessionPlan" | "subTargets" | "mentoring"> | null,
    ): void {
        if (legacyOverrides) {
            request.sessionPlan = legacyOverrides.sessionPlan;
            request.subTargets = legacyOverrides.subTargets;
            request.mentoring = legacyOverrides.mentoring;
        }
    }
}
